/**
 * EditTopToolbar — Tank Redesign Editor variant A top toolbar.
 *
 * Source: design_handoff_tank/tank-redesign/screens-editor.jsx §
 * EditorTopToolbar. Replaces the broader PhotoToolbar on the Edit page.
 *
 * Layout (49px tall): file ID / prev-next / position / zoom segmented /
 * keyboard hints / reset / Skip / Done.
 *
 * Permissions are honoured by hiding actions the user can't perform
 * (same pattern as the prior PhotoToolbar). Crop mode toggling, viz
 * layer cycling, full-res viewer, delete, and the sidebar collapse
 * toggle were all on the prior toolbar — for Editor variant A they
 * move into the right-pane accordion section headers (Crop section
 * owns crop mode toggling) or are simply dropped (no longer in spec).
 */

import { Mono, Btn, Kbd } from '../../../../shared/components/redesign';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import styles from './EditTopToolbar.module.css';

export default function EditTopToolbar({
  filename,
  isReadOnly = false,
  saving = false,
  // Position
  currentIndex = null,
  totalCount = null,
  onPrev,
  onNext,
  // Permissions
  canApprovePhotos = false,
  // Actions
  onMarkDone,
  onSkip,
  onResetCrop,
  // Done + Wire — the selects-only send. `wireState`:
  //   'hidden'   — the match has no selects-only destination: no button.
  //   'armed'    — solid emerald, fires the wire send.
  //   'disarmed' — outline: the caption gate is not satisfied yet. Still
  //                clickable — the handler explains why and opens the
  //                caption editor; the server 400 remains the authority.
  onMarkDoneWire,
  wireState = 'hidden',
  wireTargetName = '',
  // Keyboard hint
  kbHint,
}) {
  return (
    <div className={styles.toolbar}>
      <Mono className={styles.filename} style={{ fontSize: 12, textTransform: 'none' }}>
        {filename || '—'}
      </Mono>
      <span className={styles.divider} />

      <Btn
        kind="ghost"
        size="sm"
        icon={<ChevronLeftIcon style={{ width: 14, height: 14 }} />}
        onClick={onPrev}
        disabled={!onPrev}
      >
        Prev
      </Btn>
      <Btn
        kind="ghost"
        size="sm"
        iconRight={<ChevronRightIcon style={{ width: 14, height: 14 }} />}
        onClick={onNext}
        disabled={!onNext}
      >
        Next
      </Btn>

      {currentIndex != null && totalCount != null && (
        <>
          <span className={styles.divider} />
          <Mono className={styles.position}>
            {currentIndex + 1} / {totalCount}
          </Mono>
        </>
      )}

      <div className={styles.spacer} />

      {kbHint && (
        <span className={styles.kbdHint}>
          <Mono className={styles.kbdLabel}>{kbHint.label}</Mono>
          {kbHint.keys?.map((k, i) => <Kbd key={i}>{k}</Kbd>)}
        </span>
      )}

      {onResetCrop && (
        <>
          <span className={styles.divider} />
          <Btn
            kind="ghost"
            size="sm"
            icon={<ArrowPathIcon style={{ width: 14, height: 14 }} />}
            onClick={onResetCrop}
            disabled={isReadOnly}
          >
            Reset
          </Btn>
        </>
      )}

      {canApprovePhotos && onSkip && (
        <Btn kind="ghost" size="sm" onClick={onSkip} disabled={isReadOnly || saving}>
          Skip
        </Btn>
      )}

      {canApprovePhotos && onMarkDone && (
        <Btn
          kind="primary"
          size="sm"
          icon={<CheckIcon style={{ width: 14, height: 14 }} />}
          onClick={onMarkDone}
          disabled={isReadOnly || saving}
        >
          {saving ? 'Saving…' : (
            <>
              Done <span className={styles.doneShortcut}>· ⌘↵</span>
            </>
          )}
        </Btn>
      )}

      {canApprovePhotos && onMarkDoneWire && wireState !== 'hidden' && (
        <Btn
          kind={wireState === 'armed' ? 'wire' : 'wireOutline'}
          size="sm"
          onClick={onMarkDoneWire}
          disabled={isReadOnly || saving}
          title={
            wireState === 'armed'
              ? `Mark done and send to ${wireTargetName || 'the wire'} (9)`
              : `Custom caption required for ${wireTargetName || 'the wire'}`
          }
        >
          Done + Wire <span className={styles.doneShortcut}>· 9</span>
        </Btn>
      )}
    </div>
  );
}
